import trackEvent from "components/tracking/trackEvent";
import { getAssistantTravelPlannerTranslations } from "../../marketing/i18n/pages/assistantTravelPlanner";
import { Assistant, ASSISTANT_NAMES, LISTING_URLS, MCP_SERVER_URL } from "./data";
import { assistantPromptHref, fill } from "./links";

export type CtaAction = "open_assistant" | "add_connector" | "copy_server_url" | "copy_prompt";

const NAMESPACE = "assistantTravelPlanner.";

/** What every section of the page needs: the assistant, its strings, its links and the click event. */
export interface PageCtx {
	assistant: Assistant;
	/** Display name: "ChatGPT" / "Claude". */
	name: string;
	/** A string of the namespace; `{assistant}` is always filled in, `vars` fill the rest. */
	tr: (key: string, vars?: Record<string, string | number>) => string;
	/** The assistant's own variant of a string: `<assistant>.<key>`. */
	trA: (key: string, vars?: Record<string, string | number>) => string;
	/** The assistant's directory listing: where every "add Stippl" button goes. */
	listingUrl: string;
	/** Opens the assistant with `prompt` prefilled (and the connector setup sentence appended). */
	promptHref: (prompt: string) => string;
	trackCta: (placement: string, action: CtaAction) => void;
}

export function createPageCtx(assistant: Assistant, lang: string): PageCtx {
	const translations = getAssistantTravelPlannerTranslations(lang);
	const english = getAssistantTravelPlannerTranslations("en");
	const name = ASSISTANT_NAMES[assistant];
	// English is the fallback so a locale file that lags behind en.json shows English, never a raw key.
	const tr: PageCtx["tr"] = (key, vars = {}) => fill(translations[NAMESPACE + key] ?? english[NAMESPACE + key] ?? key, { assistant: name, ...vars });
	const trA: PageCtx["trA"] = (key, vars) => tr(`${assistant}.${key}`, vars);
	const listingUrl = LISTING_URLS[assistant];
	const setup = trA("prompt.setup", { mcpUrl: MCP_SERVER_URL, connectorUrl: listingUrl });
	return {
		assistant,
		name,
		tr,
		trA,
		listingUrl,
		promptHref: (prompt) => assistantPromptHref(assistant, prompt, setup),
		trackCta: (placement, action) => trackEvent("plugin_page_cta_clicked", { client: assistant, placement, action }, "marketing_site"),
	};
}
